import { api, CustomAxiosRequestConfig } from "@/shared/api/client";
import {
  PageOffersGetsDTO,
  OfferDTO,
  OfferCreateDTO,
  OfferUpdateDTO
} from "@/shared/types/financial";

export interface OfferFileDTO {
  id: string;
  name: string;
  ext: string;
  type: string;
  size: number;
  createdAt: string;
}

export const offerService = {
  getAll: async (params: { page: number; size: number; sort?: string; name?: string }): Promise<PageOffersGetsDTO> => {
    const response = await api.get<PageOffersGetsDTO>("/offers", {
      params: {
        page: params.page - 1,
        size: params.size,
        sort: params.sort || "name,asc",
        name: params.name
      },
    });
    return response.data;
  },

  getToolsOffers: async (signal?: AbortSignal): Promise<OfferDTO[]> => {
    const response = await api.get<OfferDTO[] | { content?: OfferDTO[] }>("/offers/tools", {
      signal,
    });
    return Array.isArray(response.data) ? response.data : (response.data?.content || []);
  },

  getById: async (id: string): Promise<OfferDTO> => {
    const response = await api.get<OfferDTO>(`/offers/${id}`);
    return response.data;
  },

  create: async (data: OfferCreateDTO): Promise<OfferDTO> => {
    const response = await api.post<OfferDTO>("/offers", data);
    return response.data;
  },

  update: async (id: string, data: OfferUpdateDTO): Promise<OfferDTO> => {
    const response = await api.put<OfferDTO>(`/offers/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/offers/${id}`);
  },

  // Media endpoints
  uploadMedia: async (id: string, files: File[]): Promise<OfferFileDTO[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    const response = await api.post<OfferFileDTO[]>(`/offers/${id}/media`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  getMediaList: async (id: string): Promise<OfferFileDTO[]> => {
    const response = await api.get<OfferFileDTO[]>(`/offers/${id}/media`);
    // The backend returns Set<FileGetDTO> which serializes as array
    return Array.isArray(response.data) ? response.data : [];
  },

  getMediaFile: async (offerId: string, mediaId: string): Promise<string> => {
    const response = await api.get(`/offers/${offerId}/media/${mediaId}`, {
      responseType: "blob",
    });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(response.data);
    });
  },

  getMediaBlob: async (offerId: string, mediaId: string): Promise<Blob> => {
    const response = await api.get(`/offers/${offerId}/media/${mediaId}`, {
      responseType: "blob",
    });
    return response.data;
  },

  deleteMedia: async (offerId: string, mediaId: string): Promise<void> => {
    await api.delete(`/offers/${offerId}/media/${mediaId}`);
  },

  // Manual endpoints
  uploadManual: async (id: string, file: File): Promise<OfferFileDTO> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post<OfferFileDTO>(`/offers/${id}/manual`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  uploadManuals: async (id: string, files: File[]): Promise<OfferFileDTO[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    const response = await api.post<OfferFileDTO[]>(`/offers/${id}/manuals`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return Array.isArray(response.data) ? response.data : [];
  },

  listManuals: async (offerId: string): Promise<OfferFileDTO[]> => {
    const response = await api.get<OfferFileDTO[]>(`/offers/${offerId}/manuals`);
    return Array.isArray(response.data) ? response.data : [];
  },

  // For /offers/{id}/manual (latest manual)
  getManualBlob: async (offerId: string): Promise<Blob> => {
    const response = await api.get(`/offers/${offerId}/manual`, {
      responseType: "blob",
    });
    return response.data;
  },

  // For /offers/{id}/manuals/{manual-id}
  getManualBlobById: async (offerId: string, manualId: string): Promise<Blob> => {
    const response = await api.get(`/offers/${offerId}/manuals/${manualId}`, {
      responseType: "blob",
    });
    return response.data;
  },

  // For /offers/{id}/manual (delete latest manual)
  deleteManual: async (offerId: string): Promise<void> => {
    await api.delete(`/offers/${offerId}/manual`);
  },

  // For /offers/{id}/manuals/{manual-id} (delete by id)
  deleteManualById: async (offerId: string, manualId: string): Promise<void> => {
    await api.delete(`/offers/${offerId}/manuals/${manualId}`);
  },
};